/* eslint-disable @typescript-eslint/indent */
/* eslint-disable function-paren-newline */
import React, { RefObject, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import Image from 'next/image';
import Spinner from '@components/common/SmartCalendar/Spinner';
import AddedToCartPopup from '@components/common/AddedToCartPopup/AddedToCartPopup';
import sessionStorageKeys from '@constants/sessionStorageKeys';
import { CartItemDetails } from '@customTypes/cart-items';
import useSessionStorage from '@hooks/useSessionStorage';
import { store } from '@context/store';
import { getDateWithSlashes, getMonthAndYear, isWeekend } from '@helpers/date';
import HotelDetails from '@customTypes/hotel-details';
import { ShouldShowFairPricingFromHotelDetails } from '@utils/fairPricing';
import { useCookies } from 'react-cookie';
import User from '@customTypes/user';
import AvailabilityWidgetDLP from '@components/AvailabilityWidget/AvailabilityWidgetDLP';
import FixedBottomBanner from '@components/common/FixedBottomBanner/FixedBottomBanner';
import BottomDrawer from '@components/common/BottomDrawer/BottomDrawer';
import { CheckoutInfo } from '@customTypes/checkout-info';
import { isAnyProductAvailable } from '@helpers/product';
import borderColors from '@constants/smartCalendar';
import SmartCalendar from '@components/common/SmartCalendar/SmartCalendar';
import ProductDetails from '@components/ProductDetails/ProductDetails';
import YourCart from '@components/YourCart/YourCart';
import { productCategoryClicked, selectADateFirstError } from '@events/DLP';
import showClosedForSeasonBanner from '@helpers/showClosedForSeasonBanner';
import SameDayBookingWarning from '@components/Popups/SameDayBookingWarning/SameDayBookingWarning';
import Currency from '@components/common/Currrency/Currency';
import actionTypes from '@context/actionTypes';
import { format } from 'date-fns';
import { AMPLITUDE_EXPERIMENT_VARIANT_NAME } from '@constants/amplitudeExperiments';
import YourCartWithSTP from '@components/STP/YourCart/YourCartWithSTP';
import AVAILABILITY from '@constants/product';
import ProductDetailsWithSTP from '@components/STP/ProductDetails/ProductDetailsWithSTP';
import { filterProductsByCategory, getUniqueProductCategories } from '@helpers/filters';
import { useRouter } from 'next/router';
import { useAmplitudeApplyProductCategoryTracker } from '@hooks/useAmplitudeTrackers';
import { ProductDetails as ProductDetailsData } from '../../types/product-details';
import calendarIcon from '../../assets/images/calendar-icon-new.svg';
import { getCartItems, getCheckoutInfo, getProductDetails } from '../../utils/services';

type Props = {
  productDetails: ProductDetailsData | undefined;
  hotelId: number | undefined;
  hotelDetails: HotelDetails;
  urlDate: string | undefined;
  productSectionRef: RefObject<HTMLDivElement> | null;
  productsRef: RefObject<HTMLDivElement> | null;
  scrollToBookingSection: () => void;
  setProductDetails: React.Dispatch<React.SetStateAction<ProductDetailsData | undefined>>;
  productCategory: string | null;
  handleCategoryChange: (category: string) => void;
};

export default function BookingSection({
  productDetails,
  hotelId,
  hotelDetails,
  urlDate,
  productSectionRef,
  productsRef,
  setProductDetails,
  scrollToBookingSection,
  productCategory,
  handleCategoryChange,
}: Props) {
  const [productData, setProductData] = useState<ProductDetailsData | undefined>(productDetails);
  const [isSpinnerLoading, setIsSpinnerLoading] = useState<boolean>(false);
  const [isAddedToCartPopupOpen, setIsAddedToCartPopupOpen] = useState<boolean>(false);
  const [isYourCartExpanded, setIsYourCartExpanded] = useState<boolean>(false);
  const [showDateWarning, setShowDateWarning] = useState<boolean>(false);
  const [cartData, setCartData] = useState<CartItemDetails>();
  const [borderColor, setBorderColor] = useState<string>(borderColors.DEFAULT);
  const [viewProductEventFired, setViewProductEventFired] = useState<boolean>(false);
  const [checkoutDetails, setCheckoutDetails] = useState<CheckoutInfo>();
  const [mainSectionHeight, setMainSectionHeight] = useState<number>(0);
  const [selectedProductCategory, setSelectedProductCategory] = useState<string>(
    productCategory || 'All',
  );

  const router = useRouter();
  const { getItem } = useSessionStorage();
  const cartId = getItem(sessionStorageKeys.CART_ID);
  const smartCalendarDate = getItem(sessionStorageKeys.SMART_CALENDAR_DATE);
  const { dispatch, state } = useContext(store);
  const { searchDate, pricingMarkdownExperiment, fixedDateSelectorExperiment } = state;
  const [cookies] = useCookies();
  const user: User = cookies.userInformation;
  const showFairPricing = ShouldShowFairPricingFromHotelDetails(hotelDetails);

  const sortedProducts = useMemo(() => {
    const hotspotProducts = productData?.products.filter(
      (product) => product.is_hot_spot && product.availability === AVAILABILITY.AVAILABLE,
    );
    const availableProducts = productData?.products.filter(
      (product) => !product.is_hot_spot && product.availability === AVAILABILITY.AVAILABLE,
    );
    const unavailableProducts = productData?.products.filter(
      (product) => product.availability !== AVAILABILITY.AVAILABLE,
    );

    const hotspotOrder = hotspotProducts?.sort((a, b) => a.sort_order - b.sort_order);

    const availableOrder = availableProducts?.sort((a, b) => a.sort_order - b.sort_order);

    const unavailableOrder = unavailableProducts?.sort((a, b) => a.sort_order - b.sort_order);

    const products = [
      ...(hotspotOrder ?? []),
      ...(availableOrder ?? []),
      ...(unavailableOrder ?? []),
    ];

    return {
      ...productData,
      products,
    };
  }, [productData]);

  const amplitudeApplyProductCategoryFilter = useAmplitudeApplyProductCategoryTracker('dlp');

  const getProductsByCategory = useCallback(
    (category: string) => filterProductsByCategory(sortedProducts.products, category),
    [sortedProducts.products],
  );

  const categories = useMemo(
    () => getUniqueProductCategories(sortedProducts.products),
    [sortedProducts.products],
  );

  const filteredProducts = useMemo(() => {
    const category = selectedProductCategory ?? 'All';
    const p = getProductsByCategory(category);
    if (p.length) return p;
    if (category !== 'All') {
      setSelectedProductCategory('All');
      return getProductsByCategory('All');
    }
    return [];
  }, [getProductsByCategory, setSelectedProductCategory, selectedProductCategory]);

  const onCategoryClick = (category: string) => {
    setSelectedProductCategory(category);
    handleCategoryChange(category);

    // Event
    productCategoryClicked(category);
    amplitudeApplyProductCategoryFilter(category);
  };

  const setConfirmedDate = useCallback(
    (date: Date | undefined) => {
      dispatch({
        type: actionTypes.SET_SEARCH_DATE,
        payload: date,
      });
    },
    [dispatch],
  );

  // Sync state when user navigates with back/forward buttons
  useEffect(() => {
    if (router.query.product_category) {
      setSelectedProductCategory(router.query.product_category as string);
    } else {
      setSelectedProductCategory('All');
    }
  }, [router.query.product_category]);

  useEffect(() => {
    (async () => {
      try {
        if (hotelId && searchDate) {
          setIsSpinnerLoading(true);
          const productDetailsData = await getProductDetails(
            hotelId,
            getDateWithSlashes(searchDate),
          );
          setProductData(productDetailsData);
          setProductDetails(productDetailsData);
          setIsSpinnerLoading(false);
        }
      } catch (error) {
        setIsSpinnerLoading(false);
        // TODO: Display error message here
      }
    })();
  }, [cartId, searchDate, hotelId, setProductDetails]);

  useEffect(() => {
    setProductData(productDetails);
  }, [productDetails]);

  useEffect(() => {
    if (urlDate) {
      const [y, m, d] = urlDate.split('-');
      if (y && m && d) {
        setConfirmedDate(new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10)));
      } else {
        setConfirmedDate(new Date(urlDate));
      }
    }
  }, [setConfirmedDate, urlDate]);

  useEffect(() => {
    setTimeout(() => {
      const mainSection = document.getElementById('main-section');
      if (mainSection) {
        setMainSectionHeight(mainSection.offsetHeight);
      }
    }, 2000);
  }, [hotelId]);

  useEffect(() => {
    if (productDetails && !viewProductEventFired) {
      setViewProductEventFired(true);
    }
  }, [hotelDetails, productDetails, viewProductEventFired]);

  useEffect(() => {
    if (smartCalendarDate) {
      setConfirmedDate(new Date(smartCalendarDate));
    }
  }, [setConfirmedDate, smartCalendarDate]);

  const waitFor = async (milliseconds: number) =>
    new Promise((resolve) => {
      setTimeout(() => {
        resolve(1);
      }, milliseconds);
    });

  useEffect(() => {
    if (cartId) {
      waitFor(500).then(async () => {
        const cartItems = await getCartItems(cartId, user);
        setCartData(cartItems);
      });
    }
  }, [cartId, user]);

  useEffect(() => {
    if (showDateWarning) {
      // Event
      selectADateFirstError();

      setBorderColor(borderColors.RED);
    } else {
      setBorderColor(borderColors.DEFAULT);
    }
  }, [showDateWarning]);

  useEffect(() => {
    (async () => {
      try {
        if (
          cartData &&
          cartData?.items.length > 0 &&
          cartData?.items[0] &&
          cartData?.items[0].hotel_id
        ) {
          const checkoutData = await getCheckoutInfo(cartId, user);
          if (checkoutData && checkoutData.extracted_items.length > 0) {
            setCheckoutDetails(checkoutData);
          }
        } else {
          setCheckoutDetails(undefined);
        }
      } catch (error: any) {
        // Ignore error
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartData]);

  const renderAvailabilityWidget = () => {
    if (
      smartCalendarDate &&
      isWeekend(new Date(smartCalendarDate)) &&
      productDetails &&
      !isAnyProductAvailable(productDetails.products)
    ) {
      return (
        <div className="d:w-2/3 d:mb-4 d:-mt-4">
          <AvailabilityWidgetDLP cityID={hotelDetails.city_id} />
        </div>
      );
    }

    return null;
  };

  return (
    <>
      <div
        className="px-0 flex flex-col mx-auto w-full mt-8 text-rp-primary-black d:mt-0 d:w-full"
        id="booking-section"
        data-testid="booking-section"
      >
        {/* Smart Calendar */}
        {fixedDateSelectorExperiment !== AMPLITUDE_EXPERIMENT_VARIANT_NAME && (
          <div
            className="flex flex-col w-full border-b border-rp-gray-divider pb-6 d:w-full d:pt-6 d:border-b-0"
            id="smart-calendar-container"
          >
            <p className="text-xl font-rp-pn-semi-bold text-rp-primary-black mb-3">Select a date</p>
            {hotelDetails.closed_for_season &&
              showClosedForSeasonBanner(hotelDetails.reopen_date, searchDate) && (
                <div className="flex items-center mb-2 tracking-0.3 d:hidden">
                  <p className="text-rp-red text-13 font-rp-pn-semi-bold">CLOSED FOR THE SEASON</p>
                  {hotelDetails.reopen_date && (
                    <p className="-mt-3 mx-1.5 text-2xl text-rp-dark-gray-2">.</p>
                  )}
                  {hotelDetails.reopen_date && (
                    <p className="text-sm text-rp-dark-gray-2">
                      {`Opens ${getMonthAndYear(hotelDetails.reopen_date)}`}
                    </p>
                  )}
                </div>
              )}
            <div className="flex flex-row w-full">
              {/* Adding border-red-600 text here so that it is included by purge css */}
              <div
                className={`border border-solid py-2 px-5 rounded-xl flex items-center text-base font-rp-pn-regular h-12 w-full d:text-sm d:w-3/5 d:mr-7 shadow-smartCalendar ${borderColor}`}
              >
                <div className="pr-5 pt-2 mt-0 h-9 w-9">
                  <Image src={calendarIcon} alt="calendar" />
                </div>

                <SmartCalendar
                  setConfirmedDate={setConfirmedDate}
                  confirmedDate={searchDate}
                  productDetails={sortedProducts}
                  hotelId={hotelId}
                  setShowDateWarning={setShowDateWarning}
                  urlDate={urlDate}
                  setBorderColor={setBorderColor}
                  setCartData={setCartData}
                  productsRef={productsRef}
                />
              </div>

              {hotelDetails.closed_for_season &&
                showClosedForSeasonBanner(hotelDetails.reopen_date, searchDate) && (
                  <div className="hidden mb-2 tracking-0.3 d:flex d:flex-col d:items-start d:justify-center">
                    <p className="text-rp-red text-13 font-rp-pn-semi-bold">
                      CLOSED FOR THE SEASON
                    </p>
                    {hotelDetails.reopen_date && (
                      <p className="text-sm text-rp-dark-gray-2">
                        {`Open For Booking: ${getMonthAndYear(hotelDetails.reopen_date, true)}`}
                      </p>
                    )}
                  </div>
                  // eslint-disable-next-line @typescript-eslint/indent
                )}
            </div>

            {/* HOT SPOT */}
            {!searchDate && hotelDetails.hot_spot ? (
              <div className="font-medium leading-14px mt-4 text-rp-hot-spot text-sm -tracking-0.28px">
                Save{' '}
                {hotelDetails.hot_spot.discount_type === 'cash' ? (
                  <Currency
                    value={hotelDetails.hot_spot.discount}
                    isoCodeClasses="font-medium leading-14px text-rp-hot-spot text-sm -tracking-0.28px"
                    valueClasses="font-medium leading-14px text-rp-hot-spot text-sm -tracking-0.28px"
                  />
                ) : (
                  `${hotelDetails.hot_spot.discount}%`
                )}{' '}
                when you book between{' '}
                {format(new Date(hotelDetails.hot_spot.start_visit_date), 'MM/dd/yyyy')} -
                {format(new Date(hotelDetails.hot_spot.end_visit_date), 'MM/dd/yyyy')}
              </div>
            ) : null}

            {showDateWarning && <p className="text-red-600 pt-1">Please choose date</p>}
          </div>
        )}

        {renderAvailabilityWidget()}
        {/* Product Details */}
        <div
          data-testid="products-section"
          className="relative d:border-t d:border-rp-gray-divider d:pt-2 d:mt-2 d:pb-10"
          id="product-card-container"
          ref={productSectionRef}
        >
          {isSpinnerLoading && <Spinner />}

          <p className="text-xl font-rp-pn-semi-bold text-rp-primary-black mt-4">Products</p>

          <div
            data-testid="product-categories-filters"
            className="flex flex-row items-center mt-4 mb-6 no-scrollbar overflow-x-auto space-x-2 w-full"
          >
            {categories.map((category, index, { length }) => (
              <div key={category} className={`${index + 1 === length ? 'pr-4' : ''} flex-shrink-0`}>
                <button
                  type="button"
                  onClick={() => onCategoryClick(category)}
                  className={`${
                    category.toLowerCase() === selectedProductCategory.toLowerCase()
                      ? 'text-white bg-rp-primary-black border-rp-primary-black'
                      : 'border-rp-gray-divider'
                  } border flex font-rp-primary-black font-rp-pn-regular font-semibold items-center leading-20px rounded-solar-sm flex-shrink-0 text-sm d:py-7px d:px-spacing-md`}
                >
                  {category}
                </button>
              </div>
            ))}
          </div>

          <div className="flex flex-col mt-4 space-y-4">
            {filteredProducts &&
              filteredProducts.length > 0 &&
              filteredProducts.map((product, index) => (
                <div
                  data-testid={`product-${
                    product.is_product_time_group
                      ? product.product_time_group_id
                      : product.product_id
                  }`}
                  key={product.is_product_time_group ? product.name : product.product_id}
                >
                  {pricingMarkdownExperiment === AMPLITUDE_EXPERIMENT_VARIANT_NAME ? (
                    <ProductDetailsWithSTP
                      showFairPricing={showFairPricing}
                      products={sortedProducts.products}
                      product={product}
                      date={searchDate}
                      setConfirmedDate={setConfirmedDate}
                      setIsAddedToCartPopupOpen={setIsAddedToCartPopupOpen}
                      setShowDateWarning={setShowDateWarning}
                      hotelId={hotelId}
                      hotelDetails={hotelDetails}
                      setCartData={setCartData}
                      cartData={cartData}
                      index={index}
                    />
                  ) : (
                    <ProductDetails
                      showFairPricing={showFairPricing}
                      products={sortedProducts.products}
                      product={product}
                      date={searchDate}
                      setConfirmedDate={setConfirmedDate}
                      setIsAddedToCartPopupOpen={setIsAddedToCartPopupOpen}
                      setShowDateWarning={setShowDateWarning}
                      hotelId={hotelId}
                      hotelDetails={hotelDetails}
                      setCartData={setCartData}
                      cartData={cartData}
                      index={index}
                    />
                  )}
                </div>
              ))}
          </div>
        </div>

        <AddedToCartPopup isOpen={isAddedToCartPopupOpen} setIsOpen={setIsAddedToCartPopupOpen} />

        <BottomDrawer isOpen={isYourCartExpanded} setIsOpen={setIsYourCartExpanded}>
          {pricingMarkdownExperiment === AMPLITUDE_EXPERIMENT_VARIANT_NAME ? (
            <YourCartWithSTP
              showFairPricing={showFairPricing}
              cartData={cartData}
              setCartData={setCartData}
              cartId={cartId}
              dispatch={dispatch}
              hotelId={hotelId}
              isYourCartExpanded={isYourCartExpanded}
              setIsYourCartExpanded={setIsYourCartExpanded}
              checkoutDetails={checkoutDetails}
              hotelDetails={hotelDetails}
              setCheckoutDetails={setCheckoutDetails}
              confirmedDate={searchDate}
            />
          ) : (
            <YourCart
              showFairPricing={showFairPricing}
              cartData={cartData}
              setCartData={setCartData}
              cartId={cartId}
              dispatch={dispatch}
              hotelId={hotelId}
              isYourCartExpanded={isYourCartExpanded}
              setIsYourCartExpanded={setIsYourCartExpanded}
              checkoutDetails={checkoutDetails}
              hotelDetails={hotelDetails}
              setCheckoutDetails={setCheckoutDetails}
              confirmedDate={searchDate}
              setConfirmedDate={setConfirmedDate}
              productData={sortedProducts}
              setShowDateWarning={setShowDateWarning}
              borderColor={borderColor}
              urlDate={urlDate}
              setBorderColor={setBorderColor}
              productsRef={productsRef}
              showDateWarning={showDateWarning}
            />
          )}
        </BottomDrawer>
      </div>

      <div
        className="hidden left-2/3 overflow-visible w-96 absolute d:z-10 d:block"
        style={{ height: `${mainSectionHeight - 50}px` }}
      >
        <div data-testid="cart-section" className="flex-col sticky top-0 pt-24 -mt-24">
          {fixedDateSelectorExperiment !== AMPLITUDE_EXPERIMENT_VARIANT_NAME && (
            <p className="text-21 font-rp-pn-semi-bold text-rp-primary-black pb-4">Your Cart</p>
          )}

          {pricingMarkdownExperiment === AMPLITUDE_EXPERIMENT_VARIANT_NAME ? (
            <YourCartWithSTP
              showFairPricing={showFairPricing}
              cartData={cartData}
              setCartData={setCartData}
              cartId={cartId}
              dispatch={dispatch}
              hotelId={hotelId}
              isYourCartExpanded={isYourCartExpanded}
              setIsYourCartExpanded={setIsYourCartExpanded}
              checkoutDetails={checkoutDetails}
              hotelDetails={hotelDetails}
              setCheckoutDetails={setCheckoutDetails}
              confirmedDate={searchDate}
            />
          ) : (
            <YourCart
              showFairPricing={showFairPricing}
              cartData={cartData}
              setCartData={setCartData}
              cartId={cartId}
              dispatch={dispatch}
              hotelId={hotelId}
              isYourCartExpanded={isYourCartExpanded}
              setIsYourCartExpanded={setIsYourCartExpanded}
              checkoutDetails={checkoutDetails}
              hotelDetails={hotelDetails}
              setCheckoutDetails={setCheckoutDetails}
              confirmedDate={searchDate}
              setConfirmedDate={setConfirmedDate}
              productData={sortedProducts}
              setShowDateWarning={setShowDateWarning}
              borderColor={borderColor}
              urlDate={urlDate}
              setBorderColor={setBorderColor}
              productsRef={productsRef}
              showDateWarning={showDateWarning}
            />
          )}
        </div>
      </div>

      <FixedBottomBanner
        localDate={searchDate}
        showFairPricing={showFairPricing}
        onBookNowClick={scrollToBookingSection}
        cartData={cartData}
        hotelId={hotelId}
        cartId={cartId}
        hotelDetails={hotelDetails}
        setIsYourCartExpanded={setIsYourCartExpanded}
        checkoutDetails={checkoutDetails}
        dispatch={dispatch}
        setCartData={setCartData}
      />

      {state.showSameDayBookingWarning && (
        <SameDayBookingWarning setCartData={setCartData} checkoutDetails={checkoutDetails} />
      )}
    </>
  );
}
